import React, { useEffect, useState } from "react";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../../../components/footer";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
  useMediaQuery,
} from "@mui/material";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import PriceCheckOutlinedIcon from "@mui/icons-material/PriceCheckOutlined";
import { useTheme } from "@emotion/react";
import WorkIcon from "@mui/icons-material/Work";
import SwiperImages from "../../../components/SwiperImages";
import ListingDetails from "../../../components/ListingDetails";
import SnackAlert from "../../../components/SnackAlert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import HeaderAdmin from "../../../components/headerAdmin";

const ModalReject = ({
  modalOpen,
  setModalOpen,
  loadingButtonDelete,
  handlerDelete,
}) => {
  return (
    <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 300,
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: 4,
          p: 2,
        }}
      >
        <Typography component="h2" variant="h6" mb={2}>
          Confirmación
        </Typography>
        <Typography mb={2} fontWeight="normal">
          ¿Estás seguro que quieres rechazarlo?
        </Typography>
        <Stack flexDirection="row" justifyContent="flex-end">
          <Button
            sx={{ mr: 1.5 }}
            onClick={() => setModalOpen(false)}
            disabled={loadingButtonDelete ? true : false}
          >
            CANCELAR
          </Button>
          <LoadingButton
            loading={loadingButtonDelete}
            variant="contained"
            sx={{ color: "white" }}
            onClick={handlerDelete}
          >
            CONFIRMAR
          </LoadingButton>
        </Stack>
      </Box>
    </Modal>
  );
};

function ActionButtons({
  loadApi,
  showSnack,
  modalOpen,
  setModalOpen,
  status,
}) {
  const [isLoading, setIsLoading] = useState(false);

  const params = useParams();
  const navigate = useNavigate();

  const acceptProject = async () => {
    setIsLoading(true);
    loadApi(`superadmin/projects/${params.id}`, true, "put", {
      verified: "approved",
    })
      .then((response) => {
        showSnack(response.data.message, "success");
        setTimeout(() => {
          navigate("/dashboard/projects/pending");
        }, 2000);
      })
      .catch((e) => showSnack(e.message))
      .finally(() => setIsLoading(false));
  };

  return (
    <Stack
      component="section"
      flexDirection="row"
      gap={2}
      sx={{
        width: { xs: "85%", md: "100%" },
        marginInline: "auto",
        justifyContent: "right",
      }}
    >
      <Button
        variant="outlined"
        onClick={() => setModalOpen(true)}
        disabled={status === "rejected"}
        sx={{
          alignItems: "center",
          gap: 1,
          width: {
            xs: "100%",
            md: "fit-content",
          },
          borderRadius: 2,
          px: { xs: 0, md: 5 },
        }}
      >
        <CloseIcon />
        {status === "rejected" ? "Está rechazado" : "Rechazar"}
      </Button>
      <LoadingButton
        loading={isLoading}
        variant="contained"
        onClick={acceptProject}
        disabled={status === "approved"}
        sx={{
          alignItems: "center",
          gap: 1,
          width: {
            xs: "100%",
            md: "fit-content",
          },
          borderRadius: 2,
          color: "white",
          px: { xs: 0, md: 5 },
          "&:hover": {
            backgroundColor: "primary.300",
          },
        }}
      >
        <CheckCircleIcon />
        {status === "approved" ? "Está aceptado" : "Aceptar"}
      </LoadingButton>
    </Stack>
  );
}

function TableData({ data }) {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#0B2A41",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: "normal",
    },
  }));
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  return (
    <>
      <Grid container spacing={2}>
        {(data || []).map((elementTable, index) => {
          return (
            <Grid item xs={12} md={6} key={index}>
              <TableContainer component={Paper} key={index} sx={{ mb: 2 }}>
                <Table aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      {(elementTable?.header || []).map((headerEl, index) => {
                        return (
                          <StyledTableCell
                            key={index}
                            width={index === 0 ? "50%" : "25%"}
                            sx={{ textAlign: index !== 0 ? "right" : "left" }}
                          >
                            {headerEl}
                          </StyledTableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {elementTable.body.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="left">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.col1}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.col2}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

const DesktopVersion = ({
  detailData,
  tableInfo,
  loadApi,
  showSnack,
  modalOpen,
  setModalOpen,
}) => {
  return (
    <>
      <Stack component="section" mb={4} mt={{ xs: 3, lg: 6 }}>
        <Typography component="h3" variant="h5">
          ¿Qué es {detailData?.project?.name}?
        </Typography>
        <Typography mt={2} fontWeight="normal">
          {detailData?.project?.information}
        </Typography>
      </Stack>
      <Stack mb={4}>
        <SwiperImages data={detailData.project.gallery} slides={3} />
      </Stack>
      <Stack mb={4}>
        <TableData data={tableInfo} />
      </Stack>
      <ActionButtons
        loadApi={loadApi}
        showSnack={showSnack}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        status={detailData?.project?.verified}
      />
    </>
  );
};

const MobileVersion = ({
  detailData,
  tableInfo,
  loadApi,
  showSnack,
  modalOpen,
  setModalOpen,
}) => {
  const [selectedButton, setSelectedButton] = useState("information");

  return (
    <>
      <Stack
        sx={{
          backgroundColor: "primary.300",
          borderRadius: 2,
          mt: 4,
          mb: 4,
        }}
      >
        <Grid container>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("information")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "information" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Información
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("gallery")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "gallery" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Galería
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("metrics")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "metrics" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Métricas
            </Button>
          </Grid>
        </Grid>
      </Stack>
      {selectedButton === "information" ? (
        <Stack component="section" mb={3}>
          <Typography >
            ¿Qué es {detailData.project.name}?
          </Typography>
          <Typography
            mt={2}
            fontWeight="normal"
            sx={{ wordWrap: 'break-word', maxWidth: '100%' }}
          >
            {detailData.project.information}
          </Typography>

        </Stack>
      ) : selectedButton === "gallery" ? (
        <Stack mb={3}>
          <SwiperImages data={detailData.project.gallery} slides={1} />
        </Stack>
      ) : (
        <Stack mb={3}>
          <TableData data={tableInfo} />
        </Stack>
      )}
      <ActionButtons
        loadApi={loadApi}
        showSnack={showSnack}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        status={detailData?.project?.verified}
      />
    </>
  );
};

const RenderedSection = ({
  detailData,
  listData,
  tableInfo,
  loadApi,
  showSnack,
  modalOpen,
  setModalOpen,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Stack>
      <Box sx={{ width: "100%", height: { xs: 320, md: 400 } }}>
        <img
          src={detailData.project.photo_profile[0].url}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
      <Stack px={{ xs: 2, md: 4 }}>
        <Stack component="section" pt={5}>
          <Typography component="h2" variant="h5" mb={2}>
            {detailData.project.name}
          </Typography>
          <Typography
            component="span"
            sx={{
              backgroundColor: "secondary.typePropertySpan",
              width: "fit-content",
              px: 2,
              py: 0.5,
              borderRadius: 3,
              mb: 5,
              fontWeight: "normal",
            }}
          >
            {detailData.project.name_business}
          </Typography>
          <ListingDetails data={listData} />
        </Stack>
        {isSmallScreen ? (
          <MobileVersion
            detailData={detailData}
            tableInfo={tableInfo}
            loadApi={loadApi}
            showSnack={showSnack}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
          />
        ) : (
          <DesktopVersion
            detailData={detailData}
            tableInfo={tableInfo}
            loadApi={loadApi}
            showSnack={showSnack}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
          />
        )}
      </Stack>
    </Stack>
  );
};

function ManageProjectAdmin() {
  const navigate = useNavigate();

  const GetDetails = async () => {
    setOpenBackDrop(true);
    loadApi(`superadmin/projects/${params.id}`, true, "get")
      .then((response) => {
        const dataMapping = {
          "Inversión Minima":
            parseInt(response.data.project.min_investment).toLocaleString(
              "es-ES"
            ) + "Є",
          Invertidos:
            parseInt(response.data.project.invested).toLocaleString("es-ES") +
            "Є",
          Rentabilidad:
            parseInt(response.data.project.esti_profitability).toLocaleString(
              "es-ES"
            ) + "%",
          Completado: response.data.project.completed + "%",
          Inversores: response.data.project.cant_investors,
          "Días Restantes": response.data.diffDays,
          Objetivo:
            parseInt(response.data.project.objective).toLocaleString("es-CO") +
            "Є",
        };
        const updatedListData = listData.map((item) => ({
          ...item,
          value: dataMapping[item.title],
        }));

        const updatedTableInfo = [
          {
            header: ["Inversión", "MIN", "MAX"],
            body: [
              {
                name: "Capital Ofrecido",
                col1: parseInt(
                  response.data.project.DetailProject.min_offered_capital
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_offered_capital
                ).toLocaleString("es-CO"),
              },
              {
                name: "Participaciones a emitir",
                col1: response.data.project.DetailProject.min_stake,
                col2: response.data.project.DetailProject.max_stake,
              },
              {
                name: "Valor por participación",
                col1: parseInt(
                  response.data.project.min_investment
                ).toLocaleString("es-CO"),
                col2: parseInt(response.data.project.objective).toLocaleString(
                  "es-CO"
                ),
              },
            ],
          },
          {
            header: ["Rentabilidad Estimada", "MIN", "MAX"],
            body: [
              {
                name: "Retorno de inversión",
                col1: parseInt(
                  response.data.project.DetailProject.min_return_investment
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_return_investment
                ).toLocaleString("es-CO"),
              },
              {
                name: "Rentabilidad recursos propios",
                col1: parseInt(
                  response.data.project.esti_profitability
                ).toLocaleString("es-CO"),
                col2: parseInt(response.data.project.objective).toLocaleString(
                  "es-CO"
                ),
              },
            ],
          },
          {
            header: ["Destino Inversión", "MIN", "MAX"],
            body: [
              {
                name: "Marketing",
                col1: parseInt(
                  response.data.project.DetailProject.min_marketing
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_marketing
                ).toLocaleString("es-CO"),
              },
              {
                name: "Construcción",
                col1: parseInt(
                  response.data.project.DetailProject.min_construction
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_construction
                ).toLocaleString("es-CO"),
              },
            ],
          },
          {
            header: ["Garantías", "", "Valor"],
            body: [
              {
                name: "Inmobiliaria",
                col1: "",
                col2: response.data.project.DetailProject.warranty_economica
                  ? parseInt(
                    response.data.project.DetailProject.warranty_economica
                  ).toLocaleString("es-CO")
                  : "No disponible",
              },
              {
                name: "Económica",
                col1: "",
                col2: response.data.project.DetailProject.warranty_inmobiliaria
                  ? parseInt(
                    response.data.project.DetailProject.warranty_inmobiliaria
                  ).toLocaleString("es-CO")
                  : "No disponible",
              },
            ],
          },
        ];

        setTableInfo(updatedTableInfo);

        setListData(updatedListData);
        setDetailData(response.data);
      })
      .catch((e) => {
        navigate("/projects");
      })
      .finally(() => setOpenBackDrop(false));
  };

  // DATA
  const [detailData, setDetailData] = useState(false);
  const [listData, setListData] = useState([
    {
      title: "Inversión Minima",
      icon: LocalActivityIcon,
      value: "",
    },
    {
      title: "Invertidos",
      icon: LocalAtmIcon,
      value: "",
    },
    {
      title: "Rentabilidad",
      icon: AutoGraphIcon,
      value: "",
    },
    {
      title: "Inversores",
      icon: PersonOutlineOutlinedIcon,
      value: "",
    },
    {
      title: "Días Restantes",
      icon: WatchLaterOutlinedIcon,
      value: "",
    },
    {
      title: "Objetivo",
      icon: StarOutlinedIcon,
      value: "",
    },
  ]);
  const [tableInfo, setTableInfo] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // UTILS
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [loadingButtonDelete, setLoadingButtonDelete] = useState(false);

  const params = useParams();

  const handlerDelete = () => {
    setLoadingButtonDelete(true);
    loadApi("superadmin/projects/" + params.id, true, "put", {
      verified: "rejected",
    })
      .then((response) => {
        showSnack("Proyecto rechazado", "success");
        setModalOpen(false);
        setTimeout(() => {
          navigate("/dashboard/projects/pending");
        }, 2000);
      })
      .catch((e) => showSnack(e.message))
      .finally(() => setLoadingButtonDelete(false));
  };

  useEffect(() => {
    GetDetails();
  }, []);

  return (
    <>
      <HeaderAdmin />
      <Stack
        component="main"
        sx={{
          minHeight: "95vh",
          width: { xs: "100vw", sm: "90vw", md: "85vw", lg: "65vw" },
          marginInline: "auto",
          pb: 4,
          backgroundColor: "white",
        }}
      >
        {detailData && (
          <>
            {/* IF THE ARRAY IS EMPTY THIS WILL BE SHOW IT */}
            {detailData.error && (
              <Stack sx={{ mt: 10 }}>
                <Typography textAlign="center" variant="h5">
                  Lo sentimos, datos no encontrados
                </Typography>
              </Stack>
            )}
            {/* IN CASE THAT THE ERROR KEY DOESNT EXIST, THE DATA IS RENDERED */}
            {!detailData.error && (
              <RenderedSection
                loadApi={loadApi}
                showSnack={showSnack}
                detailData={detailData}
                listData={listData}
                tableInfo={tableInfo}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
              />
            )}
          </>
        )}
      </Stack>
      <ModalReject
        modalOpen={modalOpen}
        handlerDelete={handlerDelete}
        setModalOpen={setModalOpen}
        loadingButtonDelete={loadingButtonDelete}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

export default ManageProjectAdmin;
